import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Container, Col, Row, Modal, Card, Form, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faPlay,
  faQrcode,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";

import {
  getQR,
  getStudentsForClass,
  assignAssessment,
} from "../services/APIClient";
import { QRCodeSVG } from "qrcode.react";
import QRCodeGenerator from "./QRCodeGenerator";
import PrintAllLoginsButton from "./PrintAllLoginsButton";
import PaginationComponent from "./PaginationComponent";
import Checkbox from "./Checkbox";

import "./AssignAssessmentOverview.css";

const DYSLEXIA_SCREENER_ID = "5a680b62-c6f8-4379-8ff3-235ef4273403";
const DYSLEXIA_SCREENER_NAME = "Early Dyslexia Screener";

function DyslexiaScreenerAdminOverview(props) {
  const location = useLocation();
  const navigate = useNavigate();

  const classDetails = location.state?.classDetails?.classDetails || null;
  const className = classDetails?.name || null;
  const classid = classDetails?.id || null;
  const teachers = location.state?.teachers?.teachers || null;
  const gradeLevel = location.state?.gradeLevel?.gradeLevel || null;
  const gradeLevelOptions = location.state?.gradeLevelOptions?.gradeLevelOptions || null;
  const schoolid = location.state?.schoolid?.schoolid || props.schoolid;
  const teacherid = location.state?.teacherid?.teacherid || props.teacherid;
  const countryId = location.state?.countryId?.countryId || props.countryId;

  // ---- QR modal state ----
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeDetails, setQRCodeDetails] = useState([]);
  const [loadingQRs, setLoadingQRs] = useState(false);
  const [selectedQRStudents, setSelectedQRStudents] = useState([]);
  const [qrCurrentPage, setQrCurrentPage] = useState(1);
  const qrItemsPerPage = 6;

  // ---- Assign Screener modal state ----
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [classStudents, setClassStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectStudentId, setSelectStudentId] = useState([]);
  const [isCheckAll, setIsCheckAll] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [assignError, setAssignError] = useState(null);

  // =====================
  // QR Modal handlers
  // =====================
  const handleQRClick = async () => {
    setShowQRModal(true);
    setQRCodeDetails([]);
    setSelectedQRStudents([]);
    setQrCurrentPage(1);
    setLoadingQRs(true);

    try {
      const response = await getQR(classid);
      setQRCodeDetails(response || []);
      const allIds = (response || []).map((s) => s.id);
      setSelectedQRStudents(allIds);
    } catch (error) {
      console.error("Error fetching QR codes:", error);
    } finally {
      setLoadingQRs(false);
    }
  };

  const handleQRCheckboxChange = (studentId) => {
    setSelectedQRStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const sortedQRStudents = [...qrCodeDetails].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );

  const filteredQRDetails = qrCodeDetails.filter((s) =>
    selectedQRStudents.includes(s.id)
  );

  const qrStartIndex = (qrCurrentPage - 1) * qrItemsPerPage;
  const qrCurrentItems = filteredQRDetails.slice(
    qrStartIndex,
    qrStartIndex + qrItemsPerPage
  );

  // =====================
  // Assign Screener Modal handlers
  // =====================
  const handleAssignClick = async () => {
    setShowAssignModal(true);
    setSelectStudentId([]);
    setIsCheckAll(false);
    setAssignError(null);
    setLoadingStudents(true);

    try {
      const response = await getStudentsForClass(classid);
      const combinedStudents = [
        ...(response.additionalStudents || []),
        ...(response.students || []),
      ];
      setClassStudents(combinedStudents);
    } catch (error) {
      console.error("Error fetching students:", error);
      setClassStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleStudentCheck = (e) => {
    const { id, checked } = e.target;
    setSelectStudentId((prev) =>
      checked ? [...prev, id] : prev.filter((item) => item !== id)
    );
  };

  const handleSelectAll = () => {
    setIsCheckAll((prev) => {
      const eligible = classStudents
        .filter((s) => {
          const status = getStudentStatus(s);
          return !s.disabled && status.label !== "In Progress";
        })
        .map((s) => s.id);

      if (!prev) {
        setSelectStudentId((prevIds) => [...new Set([...prevIds, ...eligible])]);
      } else {
        setSelectStudentId((prevIds) => prevIds.filter((id) => !eligible.includes(id)));
      }
      return !prev;
    });
  };

  const handleAssignSubmit = () => {
    const studentPayload = selectStudentId.map((id) => ({ id }));
    assignAssessment(studentPayload, DYSLEXIA_SCREENER_ID, DYSLEXIA_SCREENER_NAME)
      .then(() => {
        setShowConfirmModal(false);
        setShowAssignModal(false);
        alert("Dyslexia Screener assigned successfully!");
      })
      .catch((error) => {
        console.error("Error assigning screener:", error);
        setAssignError("Failed to assign screener. Please try again.");
      });
  };

  const getSelectedStudentInfo = () => {
    return classStudents
      .filter((s) => selectStudentId.includes(s.id))
      .map((s, i) => ({
        index: i + 1,
        name: s.name,
        assessment: DYSLEXIA_SCREENER_NAME,
      }));
  };

  // =====================
  // Run Screener Modal state
  // =====================
  const [showRunModal, setShowRunModal] = useState(false);
  const [runStudents, setRunStudents] = useState([]);
  const [runQRData, setRunQRData] = useState({});
  const [loadingRunStudents, setLoadingRunStudents] = useState(false);
  const [showTeacherQRModal, setShowTeacherQRModal] = useState(false);
  const [teacherQRStudent, setTeacherQRStudent] = useState(null);

  const getSessionUrl = (studentId, studentName) => {
    const origin = window.location.hostname.includes("localhost")
      ? "http://localhost:3002"
      : window.location.origin;
    const params = new URLSearchParams({ studentId });
    if (studentName) params.set("name", studentName);
    return `${origin}/dyslexiascreenersession/?${params.toString()}`;
  };

  const handleRunClick = async () => {
    setShowRunModal(true);
    setRunStudents([]);
    setRunQRData([]);
    setLoadingRunStudents(true);

    try {
      const [studentsResponse, qrResponse] = await Promise.all([
        getStudentsForClass(classid),
        getQR(classid),
      ]);
      const combinedStudents = [
        ...(studentsResponse.additionalStudents || []),
        ...(studentsResponse.students || []),
      ];
      // Build name-keyed map from QR data so we always use the correct id/password
      const qrByName = {};
      (qrResponse || []).forEach((q) => { qrByName[q.name] = q; });

      setRunStudents(combinedStudents);
      setRunQRData(qrByName);
    } catch (error) {
      console.error("Error fetching students for run screener:", error);
      setRunStudents([]);
      setRunQRData({});
    } finally {
      setLoadingRunStudents(false);
    }
  };

  const getStudentStatus = (student) => {
    const relevant = (student.assessments || []).filter(
      (a) => a.assessmentid === DYSLEXIA_SCREENER_ID
    );

    if (relevant.length === 0) {
      return { label: "Unassigned", color: "#aaa" };
    }

    const incomplete = relevant.find((a) => !a.completed);
    if (incomplete) {
      return { label: "In Progress", color: "#E67E22", assessment: incomplete };
    }

    const completed = relevant.filter((a) => a.completed);
    if (completed.length > 0) {
      const latest = completed[completed.length - 1];
      return { label: "Completed", color: "#27AE60", assessment: latest };
    }

    return { label: "Unassigned", color: "#aaa" };
  };

  // =====================
  // View Results Modal state
  // =====================
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultsStudents, setResultsStudents] = useState([]);
  const [loadingResultsStudents, setLoadingResultsStudents] = useState(false);

  const handleResultsClick = async () => {
    setShowResultsModal(true);
    setResultsStudents([]);
    setLoadingResultsStudents(true);

    try {
      const response = await getStudentsForClass(classid);
      const combinedStudents = [
        ...(response.additionalStudents || []),
        ...(response.students || []),
      ];
      // Only show students that have at least one completed assessment
      const completedStudents = combinedStudents.filter(
        (s) => s.assessments && s.assessments.some((a) => a.completed)
      );
      setResultsStudents(completedStudents);
    } catch (error) {
      console.error("Error fetching students for results:", error);
      setResultsStudents([]);
    } finally {
      setLoadingResultsStudents(false);
    }
  };

  // =====================
  // Card definitions
  // =====================
  const cards = [
    {
      id: "assign",
      icon: faClipboardList,
      iconColor: "#E67E22",
      label: "Assign Screener",
      description: "Assign to students",
    },
    {
      id: "run",
      icon: faPlay,
      iconColor: "#2980B9",
      label: "Run Screener",
      description: "Teacher session access",
    },
    {
      id: "qr",
      icon: faQrcode,
      iconColor: "#27AE60",
      label: "Student QR Codes",
      description: "Generate student logins",
    },
    {
      id: "results",
      icon: faChartBar,
      iconColor: "#8E44AD",
      label: "View Results",
      description: "Review completed screeners",
    },
  ];

  const renderCard = (card, index) => {
    const cardContent = (
      <div
        style={{
          border: "2px solid #ddd",
          borderRadius: "8px",
          padding: "40px 20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          minHeight: "180px",
          background: "#fff",
          transition: "border-color 0.2s, box-shadow 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = card.iconColor;
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "#ddd";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <FontAwesomeIcon
          icon={card.icon}
          style={{ fontSize: "3rem", color: card.iconColor, marginBottom: "12px" }}
        />
        <p style={{ fontWeight: "600", fontSize: "1.1rem", color: "#333", marginBottom: "4px", textAlign: "center" }}>
          {card.label}
        </p>
        <p style={{ color: "#888", fontSize: "0.9rem", textAlign: "center", margin: 0 }}>
          {card.description}
        </p>
      </div>
    );

    if (card.id === "qr") {
      return (
        <Col xs={6} key={index} className="mb-4">
          <div onClick={handleQRClick}>{cardContent}</div>
        </Col>
      );
    }

    if (card.id === "assign") {
      return (
        <Col xs={6} key={index} className="mb-4">
          <div onClick={handleAssignClick}>{cardContent}</div>
        </Col>
      );
    }

    if (card.id === "run") {
      return (
        <Col xs={6} key={index} className="mb-4">
          <div onClick={handleRunClick}>{cardContent}</div>
        </Col>
      );
    }

    if (card.id === "results") {
      return (
        <Col xs={6} key={index} className="mb-4">
          <div onClick={handleResultsClick}>{cardContent}</div>
        </Col>
      );
    }

    return (
      <Col xs={6} key={index} className="mb-4">
        <Link to={card.to} className="link" style={{ textDecoration: "none" }}>
          {cardContent}
        </Link>
      </Col>
    );
  };

  return (
    <Container fluid style={{ paddingLeft: 0 }}>
      <Row className="row-position">
        <Col>
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "30px",
              marginTop: "20px",
            }}
          >
            <h2 style={{ color: "#333", marginBottom: "4px" }}>
              Dyslexia Screener Admin
            </h2>
            {className && (
              <p style={{ color: "#555", fontSize: "1.1rem", marginBottom: "0" }}>
                Class: <strong>{className}</strong>
              </p>
            )}
            <hr style={{ borderColor: "#ddd", marginBottom: "8px" }} />
            <p style={{ color: "#888", fontStyle: "italic", marginBottom: "30px" }}>
              Entry point for all screener activities
            </p>

            <Row>{cards.map((card, index) => renderCard(card, index))}</Row>
          </div>
        </Col>
      </Row>

      {/* ==================== */}
      {/* QR Code Modal        */}
      {/* ==================== */}
      <Modal show={showQRModal} onHide={() => setShowQRModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{className} Student Logins</Modal.Title>
        </Modal.Header>
        {loadingQRs ? (
          <Modal.Body>
            <div className="text-center py-4">
              <div className="big-ball-spinner"></div>
            </div>
          </Modal.Body>
        ) : (
          <>
            <Row className="p-3">
              <Col xs={12}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Check
                    type="checkbox"
                    id="qr-select-all"
                    label="Select All"
                    checked={selectedQRStudents.length === sortedQRStudents.length && sortedQRStudents.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedQRStudents(sortedQRStudents.map((s) => s.id));
                      } else {
                        setSelectedQRStudents([]);
                      }
                    }}
                  />
                </div>
                <div style={{ maxHeight: "200px", overflowY: "scroll", paddingRight: "15px" }}>
                  {sortedQRStudents.map((student) => (
                    <Card key={student.id} className="mb-2">
                      <Card.Body className="d-flex justify-content-between align-items-center">
                        <Form.Check
                          type="checkbox"
                          checked={selectedQRStudents.includes(student.id)}
                          onChange={() => handleQRCheckboxChange(student.id)}
                          label={student.name}
                        />
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </Col>
            </Row>
            <Modal.Body>
              <Row>
                {qrCodeDetails.length === 0 ? (
                  <h4>There are currently no students in this classroom.</h4>
                ) : selectedQRStudents.length === 0 ? (
                  <h4>Please select at least one student to view and print their QR codes.</h4>
                ) : (
                  qrCurrentItems.map((student) => (
                    <Col xs={4} key={student.id}>
                      <QRCodeGenerator student={student} />
                    </Col>
                  ))
                )}
                <br />
                {filteredQRDetails.length > qrItemsPerPage && (
                  <PaginationComponent
                    itemsPerPage={qrItemsPerPage}
                    totalItems={filteredQRDetails.length}
                    handlePageClick={(page) => setQrCurrentPage(page)}
                  />
                )}
              </Row>
            </Modal.Body>
          </>
        )}
        <Modal.Footer>
          {selectedQRStudents.length > 0 && (
            <PrintAllLoginsButton name={className} label="Print Selected QR Codes" qrCodeDetails={filteredQRDetails} />
          )}
          <button className="white-button" onClick={() => setShowQRModal(false)}>Close</button>
        </Modal.Footer>
      </Modal>

      {/* ========================== */}
      {/* Assign Screener Modal      */}
      {/* ========================== */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Assign Dyslexia Screener — {className}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingStudents ? (
            <div className="text-center py-4">
              <div className="big-ball-spinner"></div>
            </div>
          ) : (
            <>
              {/* Screener info */}
              <div
                style={{
                  background: "#EAF4FB",
                  borderLeft: "4px solid #2980B9",
                  padding: "10px 14px",
                  borderRadius: "4px",
                  marginBottom: "20px",
                }}
              >
                <strong style={{ color: "#2980B9" }}>{DYSLEXIA_SCREENER_NAME}</strong>
              </div>

              <hr />

              {/* Student Selection */}
              <h5 style={{ marginBottom: "12px" }}>
                Select Students ({classStudents.length})
              </h5>
              {classStudents.length === 0 ? (
                <div>
                  <h4>No students have been added to your class yet.</h4>
                  <p>
                    Please go to Classrooms and select the class you want
                    to add students to.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    {classStudents.some((s) => {
                      const status = getStudentStatus(s);
                      return !s.disabled && status.label !== "In Progress";
                    }) ? (
                      <Form.Check
                        type="checkbox"
                        id="assign-select-all"
                        label="Select All"
                        checked={isCheckAll}
                        onChange={handleSelectAll}
                      />
                    ) : (
                      <span style={{ color: "#888" }}>All students have screeners assigned</span>
                    )}
                  </div>
                  <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}></th>
                          <th>Student Name</th>
                          <th>Current Screener</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classStudents.map((student) => {
                          const status = getStudentStatus(student);
                          const isDisabled = student.disabled || status.label === "In Progress";

                          return (
                            <tr key={student.id}>
                              <td className="text-center">
                                {!isDisabled && (
                                  <Checkbox
                                    type="checkbox"
                                    id={student.id}
                                    name={student.name}
                                    handleClick={handleStudentCheck}
                                    isChecked={selectStudentId.includes(student.id)}
                                    isDisabled={false}
                                  />
                                )}
                              </td>
                              <td style={{ color: isDisabled ? "#aaa" : "#333" }}>
                                {student.name}
                              </td>
                              <td>
                                <span style={{ color: status.color, fontWeight: "500" }}>
                                  {status.label === "Unassigned" ? (
                                    <span style={{ color: "#888", fontWeight: "normal" }}>None</span>
                                  ) : (
                                    status.label
                                  )}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}

              {assignError && (
                <p className="text-danger mt-2">{assignError}</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="white-button" onClick={() => setShowAssignModal(false)}>
            Close
          </button>
          {selectStudentId.length > 0 && (
            <button
              className="green-button"
              onClick={() => setShowConfirmModal(true)}
            >
              Assign ({selectStudentId.length} student{selectStudentId.length !== 1 ? "s" : ""})
            </button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ========================== */}
      {/* Run Screener Modal         */}
      {/* ========================== */}
      <Modal show={showRunModal} onHide={() => setShowRunModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Run Screener — Teacher Session Access</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingRunStudents ? (
            <div className="text-center py-4">
              <div className="big-ball-spinner"></div>
            </div>
          ) : runStudents.length === 0 ? (
            <div>
              <h4>No students have been added to your class yet.</h4>
              <p>
                Please go to Classrooms and select the class you want
                to add students to.
              </p>
            </div>
          ) : (
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {runStudents.map((student) => {
                    const status = getStudentStatus(student);
                    const isInProgress = status.label === "In Progress";

                    return (
                      <tr key={student.id}>
                        <td style={{ verticalAlign: "middle" }}>{student.name}</td>
                        <td style={{ verticalAlign: "middle" }}>
                          <span style={{ color: status.color, fontWeight: "500" }}>
                            {status.label}
                          </span>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            {isInProgress && (
                              <button
                                className="blue-button"
                                style={{
                                  padding: "6px 14px",
                                  fontSize: "0.85rem",
                                  borderRadius: "6px",
                                  border: "none",
                                  backgroundColor: "#2980B9",
                                  color: "#fff",
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  const qrStudent = runQRData[student.name];
                                  if (qrStudent) {
                                    const url = getSessionUrl(student.id, student.name);
                                    window.open(url, "_blank");
                                  }
                                }}
                              >
                                Start Session
                              </button>
                            )}
                            {isInProgress && (
                              <button
                                style={{
                                  padding: "6px 14px",
                                  fontSize: "0.85rem",
                                  borderRadius: "6px",
                                  border: "1px solid #27AE60",
                                  backgroundColor: "#fff",
                                  color: "#27AE60",
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  setTeacherQRStudent({ ...(runQRData[student.name] || {}), studentId: student.id });
                                  setShowTeacherQRModal(true);
                                }}
                              >
                                Teacher QR Code
                              </button>
                            )}
                            {!isInProgress && (
                              <span style={{ color: "#aaa", fontSize: "0.85rem" }}>—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="white-button" onClick={() => setShowRunModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========================== */}
      {/* Teacher QR Code Modal      */}
      {/* ========================== */}
      <Modal show={showTeacherQRModal} onHide={() => setShowTeacherQRModal(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Teacher QR Code — {teacherQRStudent?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {teacherQRStudent && (
            <>
              <p style={{ color: "#555", marginBottom: "20px" }}>
                Scan this QR code to open the dyslexia screener session for this student.
              </p>
              <QRCodeSVG
                id="teacher-qr-modal-svg"
                value={getSessionUrl(teacherQRStudent.studentId, teacherQRStudent.name)}
                size={250}
                level="H"
                includeMargin={true}
                imageSettings={{
                  src: "/assets/imgs/Alpaca-logo-white-outline.png",
                  height: 60,
                  width: 48,
                }}
              />
              <p style={{ color: "#888", fontSize: "0.85rem", marginTop: "16px" }}>
                {teacherQRStudent.name}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="green-button"
            style={{
              padding: "6px 14px",
              fontSize: "0.85rem",
              borderRadius: "6px",
              border: "none",
              backgroundColor: "#27AE60",
              color: "#fff",
              cursor: "pointer",
            }}
            onClick={() => {
              const studentName = teacherQRStudent?.name || "Student";
              const svgEl = document.querySelector("#teacher-qr-modal-svg");
              const svgData = svgEl ? svgEl.outerHTML : "";

              // Use a hidden iframe to print without opening a new tab
              let printFrame = document.getElementById("teacher-qr-print-frame");
              if (!printFrame) {
                printFrame = document.createElement("iframe");
                printFrame.id = "teacher-qr-print-frame";
                printFrame.style.position = "absolute";
                printFrame.style.top = "-9999px";
                printFrame.style.left = "-9999px";
                printFrame.style.width = "0";
                printFrame.style.height = "0";
                document.body.appendChild(printFrame);
              }

              const doc = printFrame.contentDocument || printFrame.contentWindow.document;
              doc.open();
              doc.write(`
                <html>
                  <head>
                    <title>Teacher QR Code — ${studentName}</title>
                    <style>
                      body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
                      h2 { color: #333; margin-bottom: 4px; }
                      p { color: #555; }
                      .qr-container { margin: 20px auto; }
                    </style>
                  </head>
                  <body>
                    <img src="/assets/imgs/Alpaca-logo-white-outline.png" alt="Alpaca Logo" style="width:60px;margin-bottom:12px;" />
                    <h2>Teacher QR Code</h2>
                    <p><strong>${studentName}</strong></p>
                    <div class="qr-container">${svgData}</div>
                    <p style="font-size:0.8rem;color:#888;">Scan to open dyslexia screener session</p>
                  </body>
                </html>
              `);
              doc.close();

              // Wait for content to render, then print
              setTimeout(() => {
                printFrame.contentWindow.focus();
                printFrame.contentWindow.print();
              }, 300);
            }}
          >
            Print QR Code
          </button>
          <button className="white-button" onClick={() => setShowTeacherQRModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========================== */}
      {/* View Results Modal         */}
      {/* ========================== */}
      <Modal show={showResultsModal} onHide={() => setShowResultsModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>View Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: "#555", fontStyle: "italic", marginBottom: "20px" }}>
            Review completed screeners with audio playback
          </p>
          {loadingResultsStudents ? (
            <div className="text-center py-4">
              <div className="big-ball-spinner"></div>
            </div>
          ) : resultsStudents.length === 0 ? (
            <div>
              <h4>No completed screeners yet.</h4>
              <p>
                Once students have completed their dyslexia screener, their results
                will appear here.
              </p>
            </div>
          ) : (
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Date Completed</th>
                    <th>Overall Score</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {resultsStudents.map((student) => {
                    const completedAssessment = student.assessments?.find((a) => a.completed);
                    const dateCompleted = completedAssessment?.completed_at
                      ? new Date(completedAssessment.completed_at).toLocaleDateString("en-IE", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "NYS";
                    const score = completedAssessment?.score != null
                      ? `${completedAssessment.score}%`
                      : "NYS";

                    return (
                      <tr key={student.id}>
                        <td style={{ verticalAlign: "middle" }}>{student.name}</td>
                        <td style={{ verticalAlign: "middle" }}>{dateCompleted}</td>
                        <td style={{ verticalAlign: "middle" }}>
                          <span style={{ fontWeight: "600" }}>{score}</span>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <button
                            className="blue-button"
                            style={{
                              padding: "6px 14px",
                              fontSize: "0.85rem",
                              borderRadius: "6px",
                              border: "none",
                              backgroundColor: "#8E44AD",
                              color: "#fff",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setShowResultsModal(false);
                              navigate("/dyslexiascreenerresults", {
                                state: {
                                  student,
                                  classDetails: { classDetails },
                                  teachers: { teachers },
                                  className,
                                  schoolid: { schoolid },
                                  teacherid: { teacherid },
                                  countryId: { countryId },
                                },
                              });
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="white-button" onClick={() => setShowResultsModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========================== */}
      {/* Confirm Assignment Modal   */}
      {/* ========================== */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Screener Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>The following students will receive the screener:</p>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Student Name</th>
                <th>Screener</th>
              </tr>
            </thead>
            <tbody>
              {getSelectedStudentInfo().map((student) => (
                <tr key={student.index}>
                  <td>{student.index}</td>
                  <td>{student.name}</td>
                  <td>{student.assessment}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <button className="white-button" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </button>
          <button className="green-button" onClick={handleAssignSubmit}>
            Confirm & Assign
          </button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default DyslexiaScreenerAdminOverview;
